import { useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Paperclip, ShieldCheck, AlertCircle } from 'lucide-react';
import { api, type UploadAirworthinessCertResponse } from '@/lib/api';
import { DocumentLink } from '@/components/document-link';

interface Props {
  lineId: string;            // public id (li_...)
  rfqPublicId: string;       // for cache invalidation
  certKey: string | null;    // R2 object key — opened via a signed URL
  certUrl: string | null;    // legacy public URL (pre private-bucket rows)
  certFilename: string | null;
  certSha256: string | null;
  certUploadedAt: string | null;
}

const ACCEPT = 'application/pdf,image/jpeg,image/png';
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIMES = new Set(['application/pdf', 'image/jpeg', 'image/png']);

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result;
      if (typeof r !== 'string') return reject(new Error('Unexpected reader result'));
      // Strip "data:<mime>;base64," prefix.
      const i = r.indexOf(',');
      resolve(i >= 0 ? r.slice(i + 1) : r);
    };
    reader.onerror = () => reject(reader.error ?? new Error('File read failed'));
    reader.readAsDataURL(file);
  });
}

export function AirworthinessCertField({ lineId, rfqPublicId, certKey, certUrl, certFilename, certSha256, certUploadedAt }: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fileBase64 = await fileToBase64(file);
      return api<UploadAirworthinessCertResponse>(
        `/api/rfqs/line-items/${lineId}/airworthiness-cert`,
        {
          method: 'POST',
          tokenGetter: () => getToken(),
          body: JSON.stringify({ fileBase64, fileName: file.name, mimeType: file.type }),
        },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rfq', rfqPublicId] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
    },
  });

  function pickFile() {
    setLocalError(null);
    inputRef.current?.click();
  }
  function onPicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file twice (after an error) still fires onChange.
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_MIMES.has(file.type)) {
      setLocalError(`Unsupported file type "${file.type || 'unknown'}". Accepted: PDF, JPEG, PNG.`);
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalError(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB — limit is 10 MB.`);
      return;
    }
    upload.mutate(file);
  }

  const hasCert = !!certKey || !!certUrl;
  const errMsg =
    localError ??
    (upload.isError ? (upload.error as { message?: string })?.message ?? 'Upload failed' : null);

  return (
    <div className="mt-2 flex items-center flex-wrap gap-2 text-xs">
      {hasCert ? (
        <>
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" aria-hidden />
          <DocumentLink
            objectKey={certKey}
            fallbackUrl={certUrl}
            label={certFilename ?? 'Airworthiness cert'}
            title={`${certFilename ?? 'Airworthiness cert'}${certSha256 ? `\nSHA-256: ${certSha256}` : ''}`}
            className="text-primary hover:underline font-medium truncate max-w-[16rem]"
          />
          {certUploadedAt && (
            <span className="text-muted-foreground">uploaded {new Date(certUploadedAt).toLocaleDateString()}</span>
          )}
          <button
            type="button"
            onClick={pickFile}
            disabled={upload.isPending}
            className="px-1.5 py-0.5 border border-border rounded hover:bg-muted disabled:opacity-50"
          >
            {upload.isPending ? 'Uploading…' : 'Replace'}
          </button>
        </>
      ) : (
        <>
          <Paperclip className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" aria-hidden />
          <button
            type="button"
            onClick={pickFile}
            disabled={upload.isPending}
            className="px-1.5 py-0.5 border border-border rounded hover:bg-muted text-muted-foreground disabled:opacity-50"
          >
            {upload.isPending ? 'Uploading…' : '+ Attach airworthiness cert'}
          </button>
          <span className="text-[11px] text-muted-foreground italic">8130-3 / EASA Form 1 / CoC — PDF, JPG, PNG up to 10 MB</span>
        </>
      )}
      {errMsg && (
        <span className="flex items-center gap-1 text-destructive">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {errMsg}
        </span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={onPicked}
        className="hidden"
      />
    </div>
  );
}
